// src/store/slices/doctorsSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { labTest } from "../../types";
import { GetLabTestCart } from "src/types/labTests.types";

// Initial state for the lab test slice
const initialState: labTest.LabTestStateType = {
  labTests: {
    isLoading: false,
    data: [],
    error: null,
  },
  addLabTestToCart: {
    isLoading: false,
    data: null,
    error: null,
  },
  getLabTestCart: {
    isLoading: false,
    data: null,
    error: null,
  },
  searchLabTests: {
    isLoading: false,
    data: null,
    error: null,
  },
  cartState: {
    data: null,
  },
  getAppointmentSlot: {
    isLoading: false,
    data: null,
    error: null,
  },
  bookTest: {
    isLoading: false,
    data: null,
    error: null,
    isSuccess: null,
    successMsg: null,
  },
  confirmTest: {
    isLoading: false,
    data: null,
    error: null,
    isSuccess: null,
    successMsg: null,
  },
  orderSummary: {
    isLoading: false,
    data: null,
    error: null,
    isSuccess: null,
    successMsg: null,
  },
  rescheduleLabTest: {
    data: null,
    isSuccess: null,
    isLoading: false,
    error: null,
  },
  cancelLabTest: {
    data: null,
    isSuccess: null,
    isLoading: false,
    error: null,
  },
};

// Define the lab test slice
const labTestSlice = createSlice({
  name: "labTest",
  initialState,
  reducers: {
    // get hospitals list
    getLabTestsRequest: (
      state: labTest.LabTestStateType,
      action: PayloadAction<any>
    ) => {
      state.labTests.isLoading = true;
      state.labTests.data = [];
      state.labTests.error = null;
    },
    getLabTestsSuccess: (
      state: labTest.LabTestStateType,
      action: PayloadAction<any>
    ) => {
      state.labTests.isLoading = false;
      state.labTests.data = action.payload;
      state.labTests.error = null;
    },
    getLabTestsError: (
      state: labTest.LabTestStateType,
      { payload: error }: PayloadAction<string>
    ) => {
      state.labTests.isLoading = false;
      state.labTests.data = [];
      state.labTests.error = error;
    },
    addLabTestToCartRequest: (
      state: labTest.LabTestStateType,
      action: PayloadAction<any>
    ) => {
      state.addLabTestToCart.isLoading = true;
      state.addLabTestToCart.data = null;
      state.addLabTestToCart.error = null;
    },
    addLabTestToCartSuccess: (
      state: labTest.LabTestStateType,
      action: PayloadAction<any>
    ) => {
      state.addLabTestToCart.isLoading = false;
      state.addLabTestToCart.data = action.payload;
      state.addLabTestToCart.error = null;
    },
    addLabTestToCartError: (
      state: labTest.LabTestStateType,
      { payload: error }: PayloadAction<string>
    ) => {
      state.addLabTestToCart.isLoading = false;
      state.addLabTestToCart.data = null;
      state.addLabTestToCart.error = error;
    },
    getLabTestCartRequest: (
      state: labTest.LabTestStateType,
      action: PayloadAction<any>
    ) => {
      state.getLabTestCart.isLoading = true;
      state.getLabTestCart.data = null;
      state.getLabTestCart.error = null;
    },
    getLabTestCartSuccess: (
      state: labTest.LabTestStateType,
      action: PayloadAction<GetLabTestCart>
    ) => {
      state.getLabTestCart.isLoading = false;
      state.getLabTestCart.data = action.payload;
      state.getLabTestCart.error = null;
    },
    getLabTestCartError: (
      state: labTest.LabTestStateType,
      { payload: error }: PayloadAction<string>
    ) => {
      state.getLabTestCart.isLoading = false;
      state.getLabTestCart.data = null;
      state.getLabTestCart.error = error;
    },
    // Update cart manually
    setCart(state, action: PayloadAction<GetLabTestCart>) {
      state.cartState.data = action.payload;
    },

    // Clear cart (e.g. after successful checkout)
    clearCart(state) {
      state.cartState.data = null;
    },
    searchLabTestsRequest: (
      state: labTest.LabTestStateType,
      action: PayloadAction<any>
    ) => {
      state.searchLabTests.isLoading = true;
      state.searchLabTests.data = null;
      state.searchLabTests.error = null;
    },
    searchLabTestsSuccess: (
      state: labTest.LabTestStateType,
      action: PayloadAction<any>
    ) => {
      state.searchLabTests.isLoading = false;
      state.searchLabTests.data = action.payload;
      state.searchLabTests.error = null;
    },
    searchLabTestsError: (
      state: labTest.LabTestStateType,
      { payload: error }: PayloadAction<string>
    ) => {
      state.searchLabTests.isLoading = false;
      state.searchLabTests.data = null;
      state.searchLabTests.error = error;
    },
    getAppointmentSlotRequest: (
      state: labTest.LabTestStateType,
      action: PayloadAction<any>
    ) => {
      state.getAppointmentSlot.isLoading = true;
      state.getAppointmentSlot.data = null;
      state.getAppointmentSlot.error = null;
    },
    getAppointmentSlotSuccess: (
      state: labTest.LabTestStateType,
      action: PayloadAction<any>
    ) => {
      state.getAppointmentSlot.isLoading = false;
      state.getAppointmentSlot.data = action.payload;
      state.getAppointmentSlot.error = null;
    },
    getAppointmentSlotError: (
      state: labTest.LabTestStateType,
      { payload: error }: PayloadAction<string>
    ) => {
      state.getAppointmentSlot.isLoading = false;
      state.getAppointmentSlot.data = null;
      state.getAppointmentSlot.error = error;
    },
    // remove item from cart
    cartListDeleteRequest: (
      state: labTest.LabTestStateType,
      action: PayloadAction<any>
    ) => {
      state.getLabTestCart.isLoading = true;
      state.getLabTestCart.error = null;
    },
    cartListDeleteSuccess: (
      state: labTest.LabTestStateType,
      action: PayloadAction<any>
    ) => {
      state.getLabTestCart.isLoading = false;
      state.getLabTestCart.data = action.payload;
    },
    cartListDeleteError: (
      state: labTest.LabTestStateType,
      { payload: error }: PayloadAction<string>
    ) => {
      state.getLabTestCart.isLoading = false;
      state.getLabTestCart.error = error;
    },

    // book test
    bookTestRequestAction: (
      state: labTest.LabTestStateType,
      action: PayloadAction<any>
    ) => {
      state.bookTest.isLoading = true;
      state.bookTest.error = null;
      state.bookTest.isSuccess = null;
    },
    bookTestSuccessAction: (
      state: labTest.LabTestStateType,
      action: PayloadAction<any>
    ) => {
      state.bookTest.isLoading = false;
      state.bookTest.data = action?.payload;
      state.bookTest.isSuccess = true;
      state.bookTest.successMsg = action?.payload?.message ?? null;
    },
    bookTestErrorAction: (
      state: labTest.LabTestStateType,
      { payload: error }: PayloadAction<string>
    ) => {
      state.bookTest.isLoading = false;
      state.bookTest.isSuccess = false;
      state.bookTest.error = error;
    },
    confirmTestRequestAction: (
      state: labTest.LabTestStateType,
      action: PayloadAction<any>
    ) => {
      state.confirmTest.isLoading = true;
      state.confirmTest.error = null;
      state.confirmTest.isSuccess = null;
    },
    confirmTestSuccessAction: (
      state: labTest.LabTestStateType,
      action: PayloadAction<any>
    ) => {
      state.confirmTest.isLoading = false;
      state.confirmTest.data = action?.payload;
      state.confirmTest.isSuccess = true;
      state.confirmTest.successMsg = action?.payload?.message ?? null;
    },
    confirmTestErrorAction: (
      state: labTest.LabTestStateType,
      { payload: error }: PayloadAction<string>
    ) => {
      state.confirmTest.isLoading = false;
      state.confirmTest.isSuccess = false;
      state.confirmTest.error = error;
    },
    orderSummaryRequest: (
      state: labTest.LabTestStateType,
      action: PayloadAction<any>
    ) => {
      state.orderSummary.isLoading = true;
      state.orderSummary.error = null;
      state.orderSummary.isSuccess = null;
    },
    orderSummarySuccess: (
      state: labTest.LabTestStateType,
      action: PayloadAction<any>
    ) => {
      state.orderSummary.isLoading = false;
      state.orderSummary.data = action.payload;
      state.orderSummary.isSuccess = true;
      state.orderSummary.successMsg = action?.payload?.message ?? null;
    },
    orderSummaryError: (
      state: labTest.LabTestStateType,
      { payload: error }: PayloadAction<string>
    ) => {
      state.orderSummary.isLoading = false;
      state.orderSummary.isSuccess = false;
      state.orderSummary.error = error;
    },
    rescheduleLabTestRequest: (
      state: labTest.LabTestStateType,
      action: PayloadAction<any>
    ) => {
      state.rescheduleLabTest.isLoading = true;
      state.rescheduleLabTest.error = null;
    },
    rescheduleLabTestSuccess: (
      state: labTest.LabTestStateType,
      action: PayloadAction<any>
    ) => {
      state.rescheduleLabTest.isLoading = false;
      state.rescheduleLabTest.data = action.payload;
    },
    rescheduleLabTestError: (
      state: labTest.LabTestStateType,
      { payload: error }: PayloadAction<string>
    ) => {
      state.rescheduleLabTest.isLoading = false;
      state.rescheduleLabTest.error = error;
    },
    cancelLabTestRequest: (
      state: labTest.LabTestStateType,
      action: PayloadAction<any>
    ) => {
      state.cancelLabTest.isLoading = true;
      state.cancelLabTest.error = null;
    },
    cancelLabTestSuccess: (
      state: labTest.LabTestStateType,
      action: PayloadAction<any>
    ) => {
      state.cancelLabTest.isLoading = false;
      state.cancelLabTest.data = action.payload;
      state.cancelLabTest.isSuccess = true;
      state.cancelLabTest.error = null;
    },
    cancelLabTestError: (
      state: labTest.LabTestStateType,
      { payload: error }: PayloadAction<string>
    ) => {
      state.cancelLabTest.isLoading = false;
      state.cancelLabTest.error = error;
      state.cancelLabTest.isSuccess = false;
    },
  },
});

// Export actions and reducer
export const {
  // get lab tests
  getLabTestsRequest,
  getLabTestsSuccess,
  getLabTestsError,
  // add lab test to cart
  addLabTestToCartRequest,
  addLabTestToCartSuccess,
  addLabTestToCartError,
  // get lab test cart
  getLabTestCartRequest,
  getLabTestCartSuccess,
  getLabTestCartError,
  // search lab tests
  searchLabTestsRequest,
  searchLabTestsSuccess,
  searchLabTestsError,
  // get appointment slot
  getAppointmentSlotRequest,
  getAppointmentSlotSuccess,
  getAppointmentSlotError,
  // set cart
  setCart,
  clearCart,
  // remove item from cart
  cartListDeleteRequest,
  cartListDeleteSuccess,
  cartListDeleteError,
  // book test
  bookTestRequestAction,
  bookTestSuccessAction,
  bookTestErrorAction,
  // confirm test
  confirmTestRequestAction,
  confirmTestSuccessAction,
  confirmTestErrorAction,
  // order summary
  orderSummaryRequest,
  orderSummarySuccess,
  orderSummaryError,
  // reschedule lab test
  rescheduleLabTestRequest,
  rescheduleLabTestSuccess,
  rescheduleLabTestError,
  // cancel lab test
  cancelLabTestRequest,
  cancelLabTestSuccess,
  cancelLabTestError,
} = labTestSlice.actions;

export default labTestSlice.reducer;
